import { createTheme, type CSSVariablesResolver } from '@mantine/core';

export const editorialTheme = createTheme({
  colors: {
    brand: [
      '#e3f2fd', '#bbdefb', '#90caf9', '#64b5f6',
      '#42a5f5', '#2196f3', '#1e88e5', '#1976d2',
      '#1565c0', '#0d47a1',
    ],
    accent: [
      '#fff3e0', '#ffe0b2', '#ffcc02', '#ffb74d',
      '#ffa726', '#ff9800', '#fb8c00', '#f57c00',
      '#ef6c00', '#e65100',
    ],
    paper: [
      '#fefdfb', '#fbfaf6', '#f8f5ed', '#f3f1ea',
      '#ede9d8', '#e6e3d8', '#d4cdb8', '#b8b3a0',
      '#8c8a7c', '#5e5d56',
    ],
    ink: [
      '#fafafc', '#e8e8ec', '#cfcfd6', '#b3b3bd',
      '#8b8b9e', '#6a6a78', '#4a4a5e', '#313143',
      '#22222f', '#1a1a2e',
    ],
    citrus: [
      '#fff3e0', '#ffe0b2', '#ffcc8b', '#ffb46a',
      '#ff9c52', '#ff7e3d', '#f06a25', '#d85a14',
      '#b8480a', '#8a3306',
    ],
  },
  primaryColor: 'brand',

  fontFamily: 'var(--tt-font-body)',
  fontFamilyMonospace: 'var(--tt-font-mono)',
  headings: {
    fontFamily: 'var(--tt-font-display)',
    fontWeight: '500',
  },

  fontSizes: {
    xs: 'var(--tt-text-xs)',
    sm: 'var(--tt-text-sm)',
    md: 'var(--tt-text-md)',
    lg: 'var(--tt-text-lg)',
    xl: 'var(--tt-text-xl)',
  },

  shadows: {
    xs: 'var(--tt-shadow-1)',
    sm: 'var(--tt-shadow-1)',
    md: 'var(--tt-shadow-2)',
    lg: 'var(--tt-shadow-3)',
    xl: 'var(--tt-shadow-3)',
  },

  white: '#ffffff',
  black: '#1a1b1e',

  radius: {
    xs: '4px',
    sm: 'var(--tt-radius-sm)',
    md: 'var(--tt-radius-md)',
    lg: 'var(--tt-radius-lg)',
    xl: '20px',
    pill: 'var(--tt-radius-pill)',
  },

  defaultRadius: 'md',
  autoContrast: true,
  respectReducedMotion: true,

  components: {
    Modal: {
      defaultProps: {
        overlayProps: { backgroundOpacity: 0.55, blur: 3 },
        transitionProps: { transition: 'fade', duration: 200 },
      },
    },

    Button: {
      defaultProps: { radius: 'pill' },
      styles: () => ({
        root: {
          height: '44px',
          paddingInline: '28px',
          fontFamily: 'var(--tt-font-body)',
          fontWeight: 600,
          letterSpacing: '0.01em',
          transition:
            'transform var(--tt-dur-2) var(--tt-ease-out), box-shadow var(--tt-dur-2) var(--tt-ease-out), background var(--tt-dur-1) var(--tt-ease-out)',
        },
      }),
    },

    // All input visual rules live in styles/components.css and target the
    // Mantine-emitted [data-variant="unstyled"] selector. One-line opt-in
    // covers every input-derived component.
    Input: { defaultProps: { variant: 'unstyled' } },
    TextInput: { defaultProps: { variant: 'unstyled' } },
    Select: {
      defaultProps: { variant: 'unstyled' },
      styles: () => ({
        dropdown: {
          background: 'var(--tt-paper-2)',
          border: '1px solid var(--tt-rule)',
          borderRadius: 'var(--tt-radius-md)',
          boxShadow: 'var(--tt-shadow-2)',
        },
        option: {
          fontFamily: 'var(--tt-font-body)',
          color: 'var(--tt-ink)',
        },
      }),
    },

    Card: {
      defaultProps: { radius: 'md', padding: 'lg', withBorder: false },
      styles: () => ({
        root: {
          background: 'var(--tt-paper-2)',
          boxShadow: 'var(--tt-shadow-1)',
          transition:
            'box-shadow var(--tt-dur-2) var(--tt-ease-out), transform var(--tt-dur-2) var(--tt-ease-out)',
        },
      }),
    },
  },
});

// Override --mantine-color-body so the page bg uses our editorial paper token
// instead of theme.white. Mantine generates this var inside a runtime
// <style data-mantine-styles> tag — cssVariablesResolver is the only API whose
// output participates in that block at equal priority.
export const editorialResolver: CSSVariablesResolver = () => ({
  variables: {},
  light: { '--mantine-color-body': 'var(--tt-paper)' },
  dark: { '--mantine-color-body': 'var(--tt-paper)' },
});
